import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Building2,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  Radio,
  ShieldAlert,
  Info,
  Compass,
} from 'lucide-react';
import { ALL_LOCATIONS, LocationRegion, COUNTRY_CONFIGS } from '../services/locationData';
import { NWSAdapter } from '../services/nwsAdapter';
import { UKAdapter } from '../services/ukAdapter';
import { CanadaAdapter } from '../services/canadaAdapter';
import { USGSAdapter } from '../services/usgsAdapter';
import { NormalizedAlert, AlertResponseMetadata } from '../types/alert';
import { AlertCard } from '../components/alerts/AlertCard';
import { AlertMap } from '../components/map/AlertMap';
import { FreshnessIndicator } from '../components/common/FreshnessIndicator';
import { DisclaimerBanner } from '../components/common/DisclaimerBanner';
import { AdSlot } from '../components/common/AdSlot';

export const LocationDetailPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const [location, setLocation] = useState<LocationRegion | null>(null);
  const [alerts, setAlerts] = useState<NormalizedAlert[]>([]);
  const [metadata, setMetadata] = useState<AlertResponseMetadata | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  useEffect(() => {
    if (!code) return;
    const cleanCode = code.toUpperCase();
    const found = ALL_LOCATIONS.find((l) => l.code.toUpperCase() === cleanCode);
    if (found) {
      setLocation(found);
      loadRegionAlerts(found, false);
    } else {
      setError(`Location code "${code}" is not a recognized state, province, or territory.`);
      setLoading(false);
    }
  }, [code]);

  const loadRegionAlerts = async (loc: LocationRegion, force: boolean) => {
    try {
      if (force) setIsRefreshing(true);
      else setLoading(true);
      setError(null);

      let fetchedAlerts: NormalizedAlert[] = [];
      let fetchedMeta: AlertResponseMetadata | null = null;

      if (loc.country === 'GB') {
        const ukRes = await UKAdapter.fetchAlerts({ forceRefresh: force });
        fetchedAlerts = ukRes.alerts;
        fetchedMeta = ukRes.metadata;
      } else if (loc.country === 'CA') {
        const caRes = await CanadaAdapter.fetchAlerts({ forceRefresh: force });
        fetchedAlerts = caRes.alerts;
        fetchedMeta = caRes.metadata;
      } else if (loc.country === 'AU') {
        // Australia earthquakes and international feed
        const usgsRes = await USGSAdapter.fetchEarthquakes({ forceRefresh: force });
        fetchedAlerts = usgsRes.alerts.filter((a) => a.country === 'AU');
        fetchedMeta = {
          source: 'Australian Regional Disruption Telemetry',
          totalAlerts: fetchedAlerts.length,
          lastChecked: new Date().toISOString(),
          isCached: false,
          cacheAgeSeconds: 0,
        };
      } else {
        // US state
        const usRes = await NWSAdapter.fetchActiveAlerts({ stateCode: loc.code, forceRefresh: force });
        fetchedAlerts = usRes.alerts;
        fetchedMeta = usRes.metadata;
      }

      setAlerts(fetchedAlerts);
      setMetadata(fetchedMeta);
    } catch (err: any) {
      setError(err.message || `Failed to fetch alerts for ${loc.name}`);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  if (!location && !loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center space-y-4">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto" />
        <h1 className="text-2xl font-black text-navy-950">Location Not Found</h1>
        <p className="text-sm text-slate-600">{error || 'Please select a valid US state from the directory.'}</p>
        <Link to="/locations" className="inline-block px-5 py-2.5 bg-navy-950 text-white rounded-xl text-xs font-bold shadow-md">
          Return to States Directory
        </Link>
      </div>
    );
  }

  const extremeCount = alerts.filter((a) => a.severity === 'Extreme').length;
  const severeCount = alerts.filter((a) => a.severity === 'Severe').length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      {/* Back button */}
      <div>
        <Link to="/locations" className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline font-bold">
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>All Regional Radars</span>
        </Link>
      </div>

      {/* Region Hero Mission-Control Banner */}
      {location && (
        <section className="bg-gradient-to-r from-navy-950 via-navy-900 to-navy-950 text-white border border-slate-800 rounded-3xl p-6 sm:p-10 shadow-lg relative overflow-hidden space-y-6">
          <div className="absolute right-0 top-0 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <span className="w-16 h-16 bg-slate-900 text-cyan-400 font-mono font-black text-2xl rounded-2xl flex items-center justify-center border border-slate-700 shadow-md">
                {location.code}
              </span>
              <div className="space-y-1">
                <div className="inline-flex items-center gap-2 text-xs font-mono text-cyan-400 uppercase">
                  <span>{COUNTRY_CONFIGS[location.country].flag}</span>
                  <span>{COUNTRY_CONFIGS[location.country].name.toUpperCase()} • {location.region.toUpperCase()}</span>
                </div>
                <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                  {location.name} Disruption Radar
                </h1>
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-300 font-mono pt-1">
                  <span>CAPITAL: <strong>{location.capital}</strong></span>
                  <span>•</span>
                  <span>LAT/LON: <strong>{location.lat.toFixed(2)}, {location.lon.toFixed(2)}</strong></span>
                  <span>•</span>
                  <span>AGENCY: <strong>{COUNTRY_CONFIGS[location.country].primaryAgency}</strong></span>
                </div>
              </div>
            </div>

            <a
              href={
                location.country === 'GB'
                  ? 'https://check-for-flooding.service.gov.uk'
                  : location.country === 'CA'
                  ? 'https://weather.gc.ca'
                  : location.country === 'AU'
                  ? 'http://www.bom.gov.au'
                  : 'https://www.weather.gov'
              }
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-slate-800/90 hover:bg-slate-700 px-4 py-2.5 rounded-xl border border-slate-700 transition-all shadow-md self-start md:self-center"
            >
              <span>Official Agency Portal</span>
              <ExternalLink className="w-3.5 h-3.5 text-cyan-400" />
            </a>
          </div>

          {/* Tracked Counties & Cities Tag Bar */}
          <div className="relative z-10 pt-4 border-t border-slate-800 flex flex-wrap items-center gap-2 text-xs">
            <span className="font-mono text-slate-400 uppercase text-[11px]">Primary Metros:</span>
            {location.keyCities.map((city) => (
              <span key={city} className="bg-slate-800/80 text-slate-200 px-2.5 py-1 rounded-md border border-slate-700/60 font-sans">
                {city}
              </span>
            ))}
            <span className="font-mono text-slate-400 uppercase text-[11px] ml-2">Monitored Counties:</span>
            {location.popularCounties.slice(0, 4).map((county) => (
              <span key={county} className="bg-slate-900/60 text-slate-300 border border-slate-800 px-2 py-0.5 rounded text-[11px] font-mono">
                {county} Co.
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Freshness Bar */}
      <FreshnessIndicator
        metadata={metadata}
        onRefresh={() => location && loadRegionAlerts(location, true)}
        isRefreshing={isRefreshing}
      />

      {/* Safety Notice */}
      <DisclaimerBanner />

      {/* State Metric Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-xs">
          <div className="text-xs font-mono text-slate-500 uppercase mb-1">Active Alerts in {location?.name}</div>
          <div className="text-3xl font-black text-navy-950 font-mono">
            {loading ? '...' : alerts.length}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">Live NWS field advisories</div>
        </div>

        <div className="bg-red-50/40 border border-red-200 rounded-2xl p-5 shadow-xs">
          <div className="text-xs font-mono font-bold text-red-700 uppercase mb-1">Extreme Hazards</div>
          <div className="text-3xl font-black text-red-600 font-mono">
            {loading ? '...' : extremeCount}
          </div>
          <div className="text-[11px] text-red-600/80 mt-1">Immediate life hazard in effect</div>
        </div>

        <div className="bg-orange-50/40 border border-orange-200 rounded-2xl p-5 shadow-xs">
          <div className="text-xs font-mono font-bold text-orange-700 uppercase mb-1">Severe Disruptions</div>
          <div className="text-3xl font-black text-orange-600 font-mono">
            {loading ? '...' : severeCount}
          </div>
          <div className="text-[11px] text-orange-600/80 mt-1">Significant local impact alert</div>
        </div>
      </div>

      {/* State Interactive Map */}
      {location && (
        <section aria-labelledby="state-map-heading" className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-cyan-500 animate-pulse" />
            <h2 id="state-map-heading" className="text-lg font-black text-navy-950">
              {location.name} Regional Radar Plot
            </h2>
          </div>
          <AlertMap
            alerts={alerts}
            center={[location.lat, location.lon]}
            zoom={location.zoom}
            height="440px"
          />
        </section>
      )}

      {/* Reserved Ad Slot */}
      <AdSlot slotId="location-detail-mid" format="horizontal" />

      {/* State Active Alerts List */}
      <section aria-labelledby="state-bulletins-heading" className="space-y-4">
        <h2 id="state-bulletins-heading" className="text-xl font-black text-navy-950">
          Official Disruption Bulletins for {location?.name}
        </h2>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-52 bg-slate-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="p-8 bg-red-50 border border-red-200 rounded-2xl text-center space-y-3">
            <AlertTriangle className="w-8 h-8 text-red-600 mx-auto" />
            <p className="text-xs sm:text-sm text-red-800">{error}</p>
            <button
              onClick={() => location && loadRegionAlerts(location, true)}
              className="px-4 py-2 bg-red-600 text-white text-xs font-bold rounded-xl"
            >
              Retry Connection
            </button>
          </div>
        ) : alerts.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center space-y-3 max-w-lg mx-auto">
            <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto border border-emerald-100">
              <CheckCircle className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-navy-950 text-base">
              No Active Weather Warnings in {location?.name}
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              The National Weather Service reports zero active storm warnings or public safety advisories for {location?.name} at this time.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {alerts.map((alert) => (
              <AlertCard key={alert.id} alert={alert} />
            ))}
          </div>
        )}
      </section>

      {/* Coverage Boundaries & Limitations */}
      <section className="bg-slate-50 border border-slate-200/90 rounded-2xl p-5 text-xs text-slate-600 space-y-2">
        <h3 className="font-bold text-navy-950 text-sm">
          Coverage Boundaries & Limitations for {location?.name}
        </h3>
        <p className="leading-relaxed">
          Alert data is transmitted from regional NWS Weather Forecast Offices (WFO) serving {location?.name}. Municipal utility interruptions, neighborhood power line failures, and local street closures not published via the federal CAP/GeoJSON alert stream may not be displayed immediately.
        </p>
      </section>
    </div>
  );
};
